import { VERY_LARGE_PACK_WARN_BYTES } from "./workspacePackStats.js";
import type { IsoGitWorkerInbound, IsoGitWorkerOutbound } from "./isoGitWorkerEntry.js";

let nextId = 1;

type OkOut = Extract<IsoGitWorkerOutbound, { ok: true }>;

export function shouldUseIsoGitWorker(largestPackBytes: number): boolean {
  return typeof Worker !== "undefined" && largestPackBytes >= VERY_LARGE_PACK_WARN_BYTES;
}

function rehydrateWorkerFailure(d: Extract<IsoGitWorkerOutbound, { ok: false }>): Error {
  const e = new Error(d.message) as Error & { code?: string; data?: unknown };
  if (d.code) e.code = d.code;
  if (d.data !== undefined) e.data = d.data;
  return e;
}

const WORKER_TIMEOUT_MS = 180_000;

async function runInFreshWorker(root: FileSystemDirectoryHandle, op: IsoGitWorkerInbound["op"]): Promise<OkOut> {
  const WorkerCtor = (await import("./isoGitWorkerEntry?worker")).default;
  const w = new WorkerCtor();
  const id = nextId++;
  return new Promise<OkOut>((resolve, reject) => {
    const timer = setTimeout(() => {
      w.terminate();
      reject(new Error("Git worker timed out."));
    }, WORKER_TIMEOUT_MS);
    const done = () => clearTimeout(timer);

    w.onmessage = (ev: MessageEvent<IsoGitWorkerOutbound>) => {
      const d = ev.data;
      if (d.type !== "result" || d.id !== id) return;
      done();
      w.terminate();
      if (d.ok) {
        resolve(d);
        return;
      }
      reject(rehydrateWorkerFailure(d));
    };
    w.onerror = (ev) => {
      done();
      w.terminate();
      reject(ev.error ?? new Error("Git worker failed to start."));
    };
    w.postMessage({ type: "run", id, root, op } satisfies IsoGitWorkerInbound);
  });
}

/** Run isomorphic-git in a dedicated worker when the largest pack is very large; fall back to main on any failure. */
export async function statusMatrixWithWorkerFallback(
  root: FileSystemDirectoryHandle,
  largestPackBytes: number,
  mainStatusMatrix: () => Promise<Array<Array<string | number>>>,
): Promise<Array<Array<string | number>>> {
  if (!shouldUseIsoGitWorker(largestPackBytes)) return mainStatusMatrix();
  try {
    const d = await runInFreshWorker(root, { kind: "statusMatrix" });
    if (Array.isArray(d.matrix)) return d.matrix;
  } catch {
    /* try main */
  }
  return mainStatusMatrix();
}

export async function logWithWorkerFallback(
  root: FileSystemDirectoryHandle,
  largestPackBytes: number,
  depth: number,
  ref: string | undefined,
  mainLog: () => Promise<Array<{ oid: string; commit: { message: string } }>>,
): Promise<Array<{ oid: string; commit: { message: string } }>> {
  if (!shouldUseIsoGitWorker(largestPackBytes)) return mainLog();
  try {
    const d = await runInFreshWorker(root, { kind: "log", depth, ref });
    if (Array.isArray(d.commits)) return d.commits;
  } catch {
    /* try main */
  }
  return mainLog();
}

export async function unstageAllWithWorkerFallback(
  root: FileSystemDirectoryHandle,
  largestPackBytes: number,
  mainUnstage: () => Promise<void>,
): Promise<void> {
  if (!shouldUseIsoGitWorker(largestPackBytes)) return mainUnstage();
  try {
    await runInFreshWorker(root, { kind: "unstageAll" });
    return;
  } catch {
    /* try main */
  }
  return mainUnstage();
}
